import { API_CONFIG } from './config';
import { httpService } from './http';
import { TIMELINE_ITEM_KINDS } from './timeline';
import { normalizeTrackedPostMutationState } from '../../utils/postMutationState';

const FEED_ENDPOINT = `${API_CONFIG.BASE_URL}/api/feed/v1/me`;
const FEED_OBJECT_TYPES = new Set(['POST', 'BAND', 'EVENT', 'SESSION', 'VENUE', 'USER']);
const DEFAULT_PAGE_LIMIT = 25;

function normalizeObjectType(value) {
    const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
    return FEED_OBJECT_TYPES.has(normalized) ? normalized : null;
}

function buildFeedUrl(options = {}) {
    const params = new URLSearchParams();
    const limit = Number.isFinite(options.limit)
        ? Math.max(1, Math.floor(options.limit))
        : DEFAULT_PAGE_LIMIT;
    params.set('limit', String(limit));
    if (typeof options.cursor === 'string' && options.cursor.trim()) {
        params.set('cursor', options.cursor.trim());
    }
    if (Array.isArray(options.types) && options.types.length > 0) {
        params.set('types', options.types.join(','));
    } else if (typeof options.types === 'string' && options.types.trim()) {
        params.set('types', options.types.trim());
    }
    if (typeof options.rankingMode === 'string' && options.rankingMode.trim()) {
        params.set('rankingMode', options.rankingMode.trim());
    }
    return `${FEED_ENDPOINT}?${params.toString()}`;
}

function normalizeFeedItem(item, generationId) {
    if (!item || typeof item !== 'object') {
        return null;
    }
    const entity = item.entity && typeof item.entity === 'object' ? item.entity : {};
    const objectType = normalizeObjectType(item.objectType || entity.objectType);
    const objectId = typeof item.objectId === 'string' && item.objectId.trim()
        ? item.objectId.trim()
        : (typeof entity.id === 'string' ? entity.id.trim() : '');
    if (!objectType || !objectId) {
        return null;
    }
    const rawTimelineItemKind = typeof item.timelineItemKind === 'string'
        ? item.timelineItemKind.trim().toUpperCase()
        : '';
    const timelineItemKind = objectType === 'POST'
        ? TIMELINE_ITEM_KINDS.POST
        : (objectType === 'USER' || rawTimelineItemKind === TIMELINE_ITEM_KINDS.SUGGESTED_USER
            ? TIMELINE_ITEM_KINDS.SUGGESTED_USER
            : TIMELINE_ITEM_KINDS.ENTITY);
    return {
        ...entity,
        id: entity.id || objectId,
        objectId,
        objectType,
        createdAt: entity.createdAt || item.createdAt,
        timelineItemKind,
        feedGenerationId: generationId || null,
        feedReasonCodes: Array.isArray(item.reasonCodes) ? item.reasonCodes : [],
    };
}

function normalizeFeedPage(page) {
    const generationId = typeof page?.generationId === 'string' ? page.generationId : null;
    const items = Array.isArray(page?.items)
        ? page.items.map((item) => normalizeFeedItem(item, generationId)).filter(Boolean)
        : [];
    return {
        generationId,
        items,
        nextCursor: typeof page?.nextCursor === 'string' && page.nextCursor.trim()
            ? page.nextCursor.trim()
            : null,
        personalized: page?.personalized === true,
        rankingVersion: page?.rankingVersion || null,
        rankingMode: page?.rankingMode || null,
    };
}

function buildTimelineItemKey(item) {
    return `${item?.timelineItemKind || 'UNKNOWN'}:${item?.objectType || 'UNKNOWN'}:${item?.id || ''}`;
}

function resolveSeedOptionValue(optionValue) {
    return typeof optionValue === 'function' ? optionValue() : optionValue;
}

function resolveSeedItems(seedItemsOption) {
    const resolvedSeedItems = resolveSeedOptionValue(seedItemsOption);
    return Array.isArray(resolvedSeedItems) ? resolvedSeedItems : [];
}

function resolveSeedPostMutationStateValue(seedPostMutationStateOption) {
    const resolvedSeedPostMutationState = resolveSeedOptionValue(seedPostMutationStateOption);
    return resolvedSeedPostMutationState && typeof resolvedSeedPostMutationState === 'object'
        ? resolvedSeedPostMutationState
        : {};
}

function areEquivalentFeedValues(left, right) {
    if (left === right) {
        return true;
    }

    const leftIsStructured = Boolean(left) && typeof left === 'object';
    const rightIsStructured = Boolean(right) && typeof right === 'object';
    if (!leftIsStructured || !rightIsStructured) {
        return false;
    }

    try {
        return JSON.stringify(left) === JSON.stringify(right);
    } catch (_) {
        return false;
    }
}

function mergeTrackedSeededPostFields(post, seededPost, trackedFields) {
    if (!trackedFields || trackedFields.size === 0) {
        return post;
    }

    let mergedPost = post;

    trackedFields.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(seededPost, field)) {
            return;
        }

        if (areEquivalentFeedValues(post?.[field], seededPost?.[field])) {
            return;
        }

        if (mergedPost === post) {
            mergedPost = {
                ...post,
                timelineItemKind: TIMELINE_ITEM_KINDS.POST,
            };
        }
        mergedPost[field] = seededPost[field];
    });

    return mergedPost;
}

function mergeSeededFeedItems(seedItems, fetchedItems, options, trackedSeedPostMutations) {
    const normalizedFetchedItems = Array.isArray(fetchedItems) ? fetchedItems : [];
    const normalizedSeedPosts = resolveSeedItems(seedItems)
        .filter((item) => item?.timelineItemKind === TIMELINE_ITEM_KINDS.POST && item?.id != null);

    if (!options?.requirePostSource || normalizedSeedPosts.length === 0) {
        return normalizedFetchedItems;
    }

    const seededPostsById = new Map(
        normalizedSeedPosts.map((post) => [String(post.id), post]),
    );

    return normalizedFetchedItems.map((item) => {
        if (item?.timelineItemKind !== TIMELINE_ITEM_KINDS.POST || item?.id == null) {
            return item;
        }

        const seededPost = seededPostsById.get(String(item.id));
        if (!seededPost) {
            return item;
        }

        const fetchedUpdatedAt = Date.parse(item?.updatedAt || item?.createdAt || '') || 0;
        const seededUpdatedAt = Date.parse(seededPost?.updatedAt || seededPost?.createdAt || '') || 0;
        if (seededUpdatedAt > fetchedUpdatedAt) {
            return {
                ...item,
                ...seededPost,
                timelineItemKind: TIMELINE_ITEM_KINDS.POST,
            };
        }

        return mergeTrackedSeededPostFields(
            item,
            seededPost,
            trackedSeedPostMutations.get(String(item.id)),
        );
    });
}

function appendUniqueItems(existingItems, nextItems) {
    const mergedByKey = new Map();
    for (const item of [...(Array.isArray(existingItems) ? existingItems : []), ...(Array.isArray(nextItems) ? nextItems : [])]) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        mergedByKey.set(buildTimelineItemKey(item), item);
    }
    return Array.from(mergedByKey.values());
}

async function fetchFeedPage(options = {}) {
    return normalizeFeedPage(await httpService.get(buildFeedUrl(options), options));
}

async function createFeedLoad(options = {}) {
    const firstPage = await fetchFeedPage(options);
    const hasSeedPostMutationState = Object.prototype.hasOwnProperty.call(options, 'seedPostMutationState');
    const trackedSeedPostMutations = normalizeTrackedPostMutationState(
        resolveSeedPostMutationStateValue(options.seedPostMutationState),
    );
    let nextSeedPostMutationState = hasSeedPostMutationState ? {} : undefined;
    let currentItems = mergeSeededFeedItems(
        options.seedItems,
        firstPage.items,
        options,
        trackedSeedPostMutations,
    );
    let nextCursor = firstPage.nextCursor;
    let generationId = firstPage.generationId;

    const timelineLoad = {
        items: currentItems,
        hasMore: Boolean(nextCursor),
        generationId,
        personalized: firstPage.personalized,
        rankingVersion: firstPage.rankingVersion,
        rankingMode: firstPage.rankingMode,
        nextSeedPostMutationState,
        async loadNextPage(onUpdate) {
            if (!nextCursor) {
                timelineLoad.hasMore = false;
                return currentItems;
            }
            const nextPage = await fetchFeedPage({
                ...options,
                cursor: nextCursor,
            });
            generationId = nextPage.generationId || generationId;
            nextCursor = nextPage.nextCursor;
            currentItems = appendUniqueItems(currentItems, nextPage.items);
            timelineLoad.items = currentItems;
            timelineLoad.hasMore = Boolean(nextCursor);
            timelineLoad.generationId = generationId;
            if (typeof onUpdate === 'function') {
                onUpdate(() => currentItems);
            }
            return currentItems;
        },
        async loadRemaining(onUpdate) {
            let safetyCounter = 0;
            while (nextCursor && safetyCounter < 200) {
                safetyCounter += 1;
                await timelineLoad.loadNextPage(onUpdate);
            }
            if (safetyCounter >= 200 && nextCursor) {
                throw new Error('Feed pagination did not converge');
            }
            return currentItems;
        },
        async resolveAll() {
            try {
                return await timelineLoad.loadRemaining();
            } catch (_) {
                return currentItems;
            }
        },
    };

    return timelineLoad;
}

function buildIdempotencyKey(operation) {
    const timestamp = Date.now().toString(36);
    const randomSuffix = Math.random().toString(36).slice(2, 12);
    return `feed:${operation}:${timestamp}:${randomSuffix}`.slice(0, 128);
}

async function recordFeedback(payload, options = {}) {
    const headers = {
        ...(options.headers || {}),
        'Idempotency-Key': options.idempotencyKey || buildIdempotencyKey('feedback'),
    };
    return httpService.post(`${FEED_ENDPOINT}/feedback`, payload, {
        ...options,
        headers,
    });
}

async function recordImpressions(payload, options = {}) {
    return httpService.post(`${FEED_ENDPOINT}/impressions`, payload, options);
}

export const feedService = {
    createFeedLoad,
    fetchFeedPage,
    recordFeedback,
    recordImpressions,
};
